import { uiText } from '../i18n/uiText';
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Download, Plus, RefreshCw, X } from 'lucide-react';
import { auditsApi, getApiErrorMessage, opsApi } from '../api';
import PageIntro from '../components/ui/PageIntro';
import Badge from '../components/ui/Badge';
import StatusBadge from '../components/ui/StatusBadge';
import { useAudits } from '../hooks/useData';
import { formatDate, getFullName } from '../utils/formatters';
import { SMART_VIEWS } from '../utils/auditSmartViews';
import { auditSignalText } from '../utils/auditSignalText';
import { useAuth } from '../store/AuthContext';
import { isAuditeeRole } from '../utils/permissions';
import AuditUpsertModal from '../components/audits/AuditUpsertModal';
import { StatStrip, Disclosure } from '../components/common/WorkspaceSummary';
import FormField, { TextInput, SelectInput } from '../components/common/FormField';
import AuditeeAuditsView from '../components/auditee/AuditeeAuditsView';
import RegistryPreferences, { useRegistrySettings } from '../components/registry/RegistryPreferences';
import RecordPreview from '../components/registry/RecordPreview';
import registryStyles from '../components/registry/RegistryWorkspace.module.css';

const REGISTRY_COLUMNS = [{key:'title',label:uiText("Аудит"),required:true},{key:'status',label:uiText("Статус")},{key:'auditor',label:uiText("Аудитор")},{key:'date',label:uiText("Плановая дата")},{key:'control',label:uiText("Контроль")},{key:'actions',label:uiText("Действия"),required:true}];

const SORT_OPTIONS = {
  scheduled_date: uiText("По плановой дате"),
  updated_desc: uiText("По последнему обновлению"),
  title: uiText("По названию"),
  status: uiText("По статусу"),
};

const LEGACY_AUDIT_KIND_LABELS = {
  supplier_conformity: uiText("Аудит соответствия поставщика"),
  equipment_rbi: uiText("Аудит оборудования (RBI)"),
  franchise_regular: uiText("Плановый франчайзинговый аудит"),
  pressure_vessel: uiText("Аудит оборудования под давлением"),
};

export default function AuditsPage() {
  const { user } = useAuth();
  const isCustomer = isAuditeeRole(user);
  const canManageAudits = ['admin', 'auditor'].includes(user?.role);
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [localError, setLocalError] = useState('');
  const [downloading, setDownloading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const { settings, setSettings, visible } = useRegistrySettings('audits', REGISTRY_COLUMNS);
  const requestedCompanyId = searchParams.get('audited_company_id');
  const requestedTemplateId = searchParams.get('template_id');
  const requestedDate = searchParams.get('scheduled_date');
  const requestedTitle = searchParams.get('title') || '';

  // Entry points from the company registry and calendar open the same creation
  // dialog with their context prefilled.  The explicit /audits/new route uses
  // this path as well, so there is one creation flow to maintain.
  React.useEffect(() => {
    const isCreateRoute = location.pathname === '/audits/new';
    if (canManageAudits && (isCreateRoute || requestedCompanyId || requestedDate) && !createOpen) {
      setCreateOpen(true);
    }
  }, [canManageAudits, createOpen, location.pathname, requestedCompanyId, requestedDate]);
  const prefillFromQuery = {
    audited_company_id: requestedCompanyId,
    template_id: requestedTemplateId,
    title: requestedTitle,
    scheduled_date: requestedDate || '',
  };

  const [taxonomy, setTaxonomy] = useState({ audit_kinds: [], subject_kinds: [], regulations: [] });

  useEffect(() => {
    if (isCustomer) return undefined;
    let alive = true;
    auditsApi.taxonomy().then((res) => {
      if (alive) setTaxonomy(res.data || { audit_kinds: [], subject_kinds: [], regulations: [] });
    }).catch(() => {
      // справочник не критичен для отображения — игнорируем ошибку
    });
    return () => { alive = false; };
  }, [isCustomer]);

  const initialFilters = useMemo(() => {
    if (isCustomer) {
      return {
        view: 'all',
        q: searchParams.get('q') || '',
        status: searchParams.get('status') || 'all',
        auditor_id: 'all',
        customer_id: 'all',
        audit_kind: 'all',
        subject_kind: 'all',
        sort_by: 'scheduled_date',
        page: Number(searchParams.get('page') || 1),
        page_size: 20,
      };
    }
    return {
      view: searchParams.get('view') || 'all',
      q: searchParams.get('q') || '',
      status: searchParams.get('status') || 'all',
      auditor_id: searchParams.get('auditor_id') || 'all',
      customer_id: searchParams.get('customer_id') || 'all',
      audit_kind: searchParams.get('audit_kind') || 'all',
      subject_kind: searchParams.get('subject_kind') || 'all',
      sort_by: searchParams.get('sort_by') || 'scheduled_date',
      page: Number(searchParams.get('page') || 1),
      page_size: Number(searchParams.get('page_size') || 20),
    };
  }, [isCustomer, searchParams]);

  const auditsState = useAudits(initialFilters);
  const response = auditsState.data || {};
  const audits = response.items || [];
  const summary = response.summary || {};
  const auditors = response.filters?.auditors || [];
  const customers = response.filters?.customers || [];
  const availableAuditKinds = (response.filters?.audit_kinds || []).map(item => ({ ...item, label: uiText(item.label) }));
  const availableSubjectKinds = (response.filters?.subject_kinds || []).map(item => ({ ...item, label: uiText(item.label) }));
  const filters = auditsState.filters || initialFilters;
  const pagination = response.pagination || {
    page: filters.page || 1,
    page_size: filters.page_size || 20,
    total: audits.length,
    total_pages: 1,
    has_prev: false,
    has_next: false,
  };
  const activeFilterChips = [
    filters.view && filters.view !== 'all' ? {
      key: 'view',
      label: SMART_VIEWS[filters.view]?.label || filters.view,
      clear: () => updateFilter({ view: 'all' }),
    } : null,
    filters.q ? {
      key: 'q',
      label: uiText("Поиск: {v0}", {v0: (filters.q)}),
      clear: () => updateFilter({ q: '' }),
    } : null,
    filters.status && filters.status !== 'all' ? {
      key: 'status',
      label: uiText("Статус: {v0}", {v0: ({
        not_started: uiText("Не начат"),
        in_progress: uiText("В работе"),
        ready_to_close: uiText("Готов к закрытию"),
        completed: uiText("Завершён"),
      }[filters.status] || filters.status)}),
      clear: () => updateFilter({ status: 'all' }),
    } : null,
    filters.auditor_id && filters.auditor_id !== 'all' ? {
      key: 'auditor_id',
      label: uiText("Аудитор: {v0}", {v0: (auditors.find((item) => String(item.id) === String(filters.auditor_id))?.full_name || filters.auditor_id)}),
      clear: () => updateFilter({ auditor_id: 'all' }),
    } : null,
    filters.customer_id && filters.customer_id !== 'all' ? {
      key: 'customer_id',
      label: uiText("Проверяемый: {v0}", {v0: (customers.find((item) => String(item.id) === String(filters.customer_id))?.full_name || filters.customer_id)}),
      clear: () => updateFilter({ customer_id: 'all' }),
    } : null,
    filters.audit_kind && filters.audit_kind !== 'all' ? {
      key: 'audit_kind',
      label: uiText("Тип: {v0}", {v0: (availableAuditKinds.find((item) => item.code === filters.audit_kind)?.label || filters.audit_kind)}),
      clear: () => updateFilter({ audit_kind: 'all' }),
    } : null,
    filters.subject_kind && filters.subject_kind !== 'all' ? {
      key: 'subject_kind',
      label: uiText("Объект: {v0}", {v0: (availableSubjectKinds.find((item) => item.code === filters.subject_kind)?.label || filters.subject_kind)}),
      clear: () => updateFilter({ subject_kind: 'all' }),
    } : null,
  ].filter(Boolean);

  const setFilters = useCallback((newFilters) => {
    auditsState.setFilters(newFilters);
  }, [auditsState.setFilters]);

  useEffect(() => {
    setFilters(initialFilters);
  }, [
    setFilters,
    initialFilters.view,
    initialFilters.q,
    initialFilters.status,
    initialFilters.auditor_id,
    initialFilters.customer_id,
    initialFilters.audit_kind,
    initialFilters.subject_kind,
    initialFilters.sort_by,
    initialFilters.page,
    initialFilters.page_size,
  ]);

  const updateFilter = (patch) => {
    const next = { ...filters, ...patch };
    if (!Object.prototype.hasOwnProperty.call(patch, 'page')) next.page = 1;
    setFilters(next);
    const cleaned = Object.fromEntries(
      Object.entries(next).filter(([, value]) => value && value !== 'all' && value !== 'scheduled_date' && value !== '' && value !== 1 && value !== 20)
    );
    if (next.view && next.view !== 'all') cleaned.view = next.view;
    if (next.sort_by && next.sort_by !== 'scheduled_date') cleaned.sort_by = next.sort_by;
    if (next.q) cleaned.q = next.q;
    setSearchParams(cleaned);
  };

  const resetFilters = () => {
    if (isCustomer) {
      auditsState.setFilters({ ...initialFilters, q: '', status: 'all', page: 1 });
    } else {
      auditsState.resetFilters();
    }
    setSearchParams({});
  };

  const clearCreateParams = (nextParams = new URLSearchParams(searchParams)) => {
    nextParams.delete('audited_company_id');
    nextParams.delete('template_id');
    nextParams.delete('title');
    nextParams.delete('scheduled_date');
    return nextParams;
  };

  const handleDownload = async () => {
    setLocalError('');
    setDownloading(true);
    try {
      const res = await opsApi.auditsXlsx();
      const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'audits_registry.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setLocalError(getApiErrorMessage(e) || uiText("Не удалось выгрузить реестр аудитов"));
    } finally {
      setDownloading(false);
    }
  };

  if (isCustomer) {
    return (
      <AuditeeAuditsView
        audits={audits}
        summary={summary}
        filters={filters}
        pagination={pagination}
        loading={auditsState.loading}
        error={auditsState.error || localError}
        updateFilter={updateFilter}
        resetFilters={resetFilters}
        reload={auditsState.reload}
      />
    );
  }

  return (
    <div className={`workspace-page ${registryStyles.workspace}`} data-density={settings.density}>
      <PageIntro
        title={uiText("Аудиты")}
        description={uiText("Реестр проверок, статусы и ближайшие даты.")}
        aside={
          <div className="workspace-side-actions">
            <button type="button" className="btn btn-outline" onClick={() => auditsState.reload()}><RefreshCw size={15} />{uiText(" Обновить")}</button>
            <button type="button" className="btn btn-outline" onClick={handleDownload} disabled={downloading}><Download size={15} /> {downloading ? uiText("Выгрузка...") : uiText("Экспорт XLSX")}</button>
            {canManageAudits ? <button type="button" className="btn btn-primary" onClick={() => process.env.REACT_APP_ENABLE_CLASSIFIER === 'true' ? navigate('/audit-preparation') : setCreateOpen(true)}><Plus size={15} />{uiText(" Новый аудит")}</button> : null}
          </div>
        }
      />

      {createOpen ? (
        <AuditUpsertModal
          mode="create"
          initialValues={prefillFromQuery}
          onClose={() => {
            setCreateOpen(false);
            if (location.pathname === '/audits/new') {
              navigate('/audits', { replace: true });
            } else if (searchParams.get('audited_company_id') || searchParams.get('scheduled_date')) {
              setSearchParams(clearCreateParams(), { replace: true });
            }
          }}
          onSaved={async () => {
            setCreateOpen(false);
            const nextFilters = {
              ...filters,
              view: 'all',
              q: '',
              status: 'all',
              auditor_id: 'all',
              customer_id: 'all',
              audit_kind: 'all',
              subject_kind: 'all',
              page: 1,
            };
            auditsState.setFilters(nextFilters);
            if (location.pathname === '/audits/new') {
              navigate('/audits', { replace: true });
            } else {
              setSearchParams(clearCreateParams(new URLSearchParams()), { replace: true });
            }
            await auditsState.reload();
          }}
        />
      ) : null}

      <StatStrip items={[
        {key:'attention', label:uiText("Требуют действий"),value:summary.attention || 0,tone:'danger',active:filters.view==='attention_needed',onClick:()=>updateFilter({view:'attention_needed'})},
        {key:'active', label:uiText("Мои активные"),value:summary.my_active ?? summary.active ?? 0,active:filters.view==='my_active',onClick:()=>updateFilter({view:'my_active'})},
        {key:'upcoming', label:uiText("В ближайшие 7 дней"),value:summary.upcoming || 0,active:filters.view==='upcoming',onClick:()=>updateFilter({view:'upcoming'})},
        {key:'completed', label:uiText("Завершены за 7 дней"),value:summary.completed_recent || 0,tone:'success',active:filters.view==='completed_recent',onClick:()=>updateFilter({view:'completed_recent'})},
      ]} />
      {auditsState.error || localError ? <div className="alert alert-error" role="alert">{auditsState.error || localError}</div> : null}
      <RegistryPreferences scope="audits" columns={REGISTRY_COLUMNS} filters={{...filters,page:1}} settings={settings} onSettings={setSettings} onApply={query => updateFilter({view:'all',q:'',status:'all',auditor_id:'all',customer_id:'all',audit_kind:'all',subject_kind:'all',sort_by:'scheduled_date',page_size:20,...Object.fromEntries(Object.entries(query).filter(([key]) => Object.prototype.hasOwnProperty.call(initialFilters,key))),page:1})} />
      <section className="ap-registry-filters" aria-label={uiText("Фильтры аудитов")}>
        <div className="ap-registry-tabs" role="group" aria-label={uiText("Подборки аудитов")}>
          {Object.values(SMART_VIEWS).map(view=><button type="button" key={view.key} className="ap-button ap-button--secondary" aria-pressed={filters.view===view.key} onClick={()=>updateFilter({view:view.key})}>{view.label}</button>)}
        </div>
        <div className="ap-registry-toolbar">
          <FormField label={uiText("Поиск аудитов")}><TextInput value={filters.q || ''} onChange={e=>updateFilter({q:e.target.value})} placeholder={uiText("Название, шаблон, аудитор или площадка")} type="search" /></FormField>
          <FormField label={uiText("Статус")}><SelectInput value={filters.status || 'all'} onChange={e=>updateFilter({status:e.target.value})}>
            <option value="all">{uiText("Все статусы")}</option><option value="not_started">{uiText("Не начат")}</option><option value="in_progress">{uiText("В работе")}</option><option value="ready_to_close">{uiText("Готов к закрытию")}</option><option value="completed">{uiText("Завершён")}</option>
          </SelectInput></FormField>
          <button type="button" className="ap-button ap-button--secondary" onClick={resetFilters}><X size={14}/>{uiText(" Сбросить фильтры")}</button>
        </div>
        <Disclosure title={uiText("Дополнительные фильтры")} hint={uiText("Аудитор, организация, тип и сортировка")}>
          <div className="ap-registry-options">
            <FormField label={uiText("Аудитор")}><SelectInput value={filters.auditor_id || 'all'} onChange={e=>updateFilter({auditor_id:e.target.value})}><option value="all">{uiText("Все аудиторы")}</option>{auditors.map(item=><option key={item.id} value={item.id}>{item.full_name}</option>)}</SelectInput></FormField>
            <FormField label={uiText("Проверяемый")}><SelectInput value={filters.customer_id || 'all'} onChange={e=>updateFilter({customer_id:e.target.value})}><option value="all">{uiText("Все площадки / клиенты")}</option>{customers.map(item=><option key={item.id} value={item.id}>{item.full_name}</option>)}</SelectInput></FormField>
            <FormField label={uiText("Тип аудита")}><SelectInput value={filters.audit_kind || 'all'} onChange={e=>updateFilter({audit_kind:e.target.value})}><option value="all">{uiText("Все типы")}</option>{availableAuditKinds.map(item=><option key={item.code} value={item.code}>{item.label}</option>)}</SelectInput></FormField>
            <FormField label={uiText("Тип объекта проверки")}><SelectInput value={filters.subject_kind || 'all'} onChange={e=>updateFilter({subject_kind:e.target.value})}><option value="all">{uiText("Все объекты")}</option>{availableSubjectKinds.map(item=><option key={item.code} value={item.code}>{item.label}</option>)}</SelectInput></FormField>
            <FormField label={uiText("Сортировка")}><SelectInput value={filters.sort_by || 'scheduled_date'} onChange={e=>updateFilter({sort_by:e.target.value})}>{Object.entries(SORT_OPTIONS).map(([value,label])=><option key={value} value={value}>{label}</option>)}</SelectInput></FormField>
            <FormField label={uiText("На странице")}><SelectInput value={filters.page_size || 20} onChange={e=>updateFilter({page_size:Number(e.target.value),page:1})}>{[10,20,50].map(size=><option key={size} value={size}>{size}</option>)}</SelectInput></FormField>
          </div>
        </Disclosure>
        {activeFilterChips.length ? <div className="active-filter-chips" aria-label={uiText("Выбранные фильтры")}><span className="workspace-muted">{uiText("Применено:")}</span>{activeFilterChips.map(chip=><span className="active-filter-chip" key={chip.key}>{chip.label}<button type="button" onClick={chip.clear} aria-label={uiText("Убрать фильтр «{v0}»", {v0: (chip.label)})}><X size={12}/></button></span>)}</div> : null}
      </section>

      {auditsState.loading ? (
        <div className="loading-center"><div className="spinner" /></div>
      ) : (() => {
          const filteredAudits = audits;
          if (filteredAudits.length === 0) {
            return (
              <div className="card">
                <div className="card-body">
                  <div className="workspace-empty-state">
                    <div className="workspace-empty-state-title">{uiText("По выбранным условиям аудиты не найдены")}</div>
                    <div className="workspace-empty-state-text">{uiText("Сбросьте часть фильтров или выберите другой смарт-вид. Созданные аудиты и данные при этом не изменятся.")}</div>
                    <div className="workspace-side-actions" style={{ marginTop: 12 }}>
                      <button type="button" className="btn btn-outline btn-sm" onClick={resetFilters}>{uiText("Сбросить фильтры")}</button>
                      {canManageAudits ? <button type="button" className="btn btn-primary btn-sm" onClick={() => process.env.REACT_APP_ENABLE_CLASSIFIER === 'true' ? navigate('/audit-preparation') : setCreateOpen(true)}>{uiText("Создать аудит")}</button> : null}
                    </div>
                  </div>
                </div>
              </div>
            );
          }
          // Карта кодов → ярлыки для отображения
          const kindLabel = {
            ...LEGACY_AUDIT_KIND_LABELS,
            ...Object.fromEntries((taxonomy.audit_kinds || []).map((k) => [k.code, uiText(k.label)])),
          };
          const subjectLabel = {
            ...Object.fromEntries((taxonomy.subject_kinds || []).map((s) => [s.code, uiText(s.label)])),
            ...Object.fromEntries(availableSubjectKinds.map((s) => [s.code, s.label])),
          };
          return (
            <div className="card ap-registry-results">
              <div className="card-header"><div className="card-title">{uiText("Реестр аудитов")}</div><div className="workspace-muted">{uiText("Показано: ")}{filteredAudits.length}{uiText(" из ")}{pagination.total || audits.length}</div></div>
              <div className="ap-table-wrap">
                <table className="ap-table">
                  <thead>
                    <tr>
                      <th>{uiText("Аудит")}</th>
                      <th data-column="status" hidden={!visible('status')}>{uiText("Статус")}</th>
                      <th data-column="auditor" hidden={!visible('auditor')}>{uiText("Аудитор")}</th>
                      <th data-column="date" hidden={!visible('date')}>{uiText("Плановая дата")}</th>
                      {!isCustomer ? <th data-column="control" hidden={!visible('control')}>{uiText("Контроль")}</th> : null}
                      <th>{uiText("Действия")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredAudits.map((audit) => (
                      <tr
                        key={audit.id}
                        data-clickable="true"
                        tabIndex={0}
                        onClick={event => { event.currentTarget.focus({ preventScroll: true }); setPreview(audit); }}
                        onKeyDown={(event) => {
                          if (event.target === event.currentTarget && (event.key === 'Enter' || event.key === ' ')) {
                            event.preventDefault();
                            setPreview(audit);
                          }
                        }}
                        aria-label={uiText("Быстрый просмотр аудита {v0}", {v0: (audit.title || audit.id)})}
                      >
                        <td data-label={uiText("Аудит")}>
                          <div className="ap-registry-title">{audit.title || uiText("Аудит #{v0}", {v0: (audit.id)})}</div>
                          <div className="ap-registry-detail">{audit.site_name || audit.meta?.external_customer_name || uiText("Площадка не указана")}</div>
                          <div className="ap-registry-detail">{kindLabel[audit.meta?.audit_type] || audit.meta?.audit_type || '—'}{subjectLabel[audit.meta?.category] ? ` · ${subjectLabel[audit.meta.category]}` : ''}</div>
                          {!isCustomer && <div className="ap-registry-detail">{uiText("Шаблон: ")}{audit.template_name || '—'}</div>}
                        </td>
                        <td data-column="status" hidden={!visible('status')} data-label={uiText("Статус")}><StatusBadge status={audit.status} type="audit" /></td>
                        <td data-column="auditor" hidden={!visible('auditor')} data-label={uiText("Аудитор")}><div>{getFullName(audit.auditor, '—')}</div><div className="ap-registry-detail">{uiText("Проверяемый: ")}{getFullName(audit.customer, audit.site_name || '—')}</div></td>
                        <td data-column="date" hidden={!visible('date')} data-label={uiText("Плановая дата")}>{formatDate(audit.scheduled_date)}</td>
                        {!isCustomer ? <td data-column="control" hidden={!visible('control')} data-label={uiText("Контроль")}>
                          <div className="workspace-chip-row">
                            {(audit.signals || []).length ? audit.signals.map((signal) => <Badge key={signal} variant="danger">{auditSignalText(signal)}</Badge>) : <Badge variant="success">{uiText("ОК")}</Badge>}
                          </div>
                        </td> : null}
                        <td data-label={uiText("Действия")} onClick={(event) => event.stopPropagation()}>
                          <div style={{display:'grid', gap:5, justifyItems:'start'}}>
                            {!isCustomer && audit.can_edit === false ? <Badge variant="neutral">{uiText("Только просмотр")}</Badge> : null}
                            <button type="button" className="btn btn-outline btn-sm" onClick={event => { event.currentTarget.focus({ preventScroll: true }); setPreview(audit); }}>{uiText("Быстрый просмотр")}</button>
                            <Link className="btn btn-outline btn-sm" to={`/audits/${audit.id}`}>
                              {audit.can_edit === false && !isCustomer ? uiText("Просмотреть") : uiText("Открыть аудит")}
                            </Link>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="card-body" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <div className="workspace-muted">{uiText("Страница ")}{pagination.page}{uiText(" из ")}{pagination.total_pages}</div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button type="button" className="btn btn-outline btn-sm" disabled={!pagination.has_prev} onClick={() => updateFilter({ page: Math.max(1, (pagination.page || 1) - 1) })}>{uiText("Назад")}</button>
                  <button type="button" className="btn btn-outline btn-sm" disabled={!pagination.has_next} onClick={() => updateFilter({ page: (pagination.page || 1) + 1 })}>{uiText("Вперёд")}</button>
                </div>
              </div>
            </div>
          );
        })()
      }
      {preview && <RecordPreview key={preview.id} record={preview} onClose={() => setPreview(null)} />}
    </div>
  );
}
